import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Visibility as PreviewIcon,
  Campaign as CampaignIcon,
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Block as InactiveIcon,
  RestartAlt as ReactivateIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import AdminChrome, {
  adminOutlineBtnSx,
  adminSolidBtnSx,
  adminStatPaperSx,
  adminSurfaceCardSx,
} from '../components/AdminChrome';
import {
  DOCUMENT,
  documentSelectMenuProps,
  documentSelectSx,
  documentSubTabsSx,
} from '../constants/documentTheme';

const audienceLeadCheckboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const audienceLeadCheckboxCheckedIcon = <CheckBoxIcon fontSize="small" sx={{ color: DOCUMENT.oxblood }} />;

const audienceLeadListPanelSx = {
  borderRadius: 0,
  border: `1px solid ${DOCUMENT.rule}`,
  bgcolor: DOCUMENT.canvas,
  color: DOCUMENT.ink,
  fontFamily: DOCUMENT.sans,
  mt: 1.5,
};

/** Compact selects for the audience picker filter row. */
const audienceFilterSelectSx = {
  ...documentSelectSx,
  '& .MuiInputLabel-root': {
    fontFamily: DOCUMENT.sans,
    fontSize: 11,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: DOCUMENT.ink3,
    '&.Mui-focused, &.MuiInputLabel-shrink': { color: DOCUMENT.oxblood },
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    bgcolor: DOCUMENT.surface,
    minHeight: 36,
    '& fieldset': { borderColor: DOCUMENT.ruleHard },
    '&:hover fieldset': { borderColor: DOCUMENT.accent },
    '&.Mui-focused fieldset': { borderColor: DOCUMENT.oxblood, borderWidth: 1 },
  },
  '& .MuiSelect-select': {
    fontFamily: DOCUMENT.sans,
    fontSize: 13,
    color: DOCUMENT.ink2,
    py: 0.75,
    minHeight: 'unset !important',
    display: 'flex',
    alignItems: 'center',
  },
};

const StatBox = ({ label, value, color = DOCUMENT.ink, onClick = null, hint = null }) => (
  <Paper
    sx={{
      ...adminStatPaperSx,
      ...(onClick
        ? {
          cursor: 'pointer',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            borderColor: color,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.04)',
          },
        }
        : {}),
    }}
    onClick={onClick || undefined}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick
      ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }
      : undefined}
  >
    <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.75rem', color, textAlign: 'center' }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3, textAlign: 'center', mt: 0.5 }}>
      {label}
    </Typography>
    {hint ? (
      <Typography sx={{ fontSize: 10, color: DOCUMENT.ink3, textAlign: 'center', mt: 0.5 }}>
        {hint}
      </Typography>
    ) : null}
  </Paper>
);

const statusColor = (status) => {
  if (status === 'running') return 'success';
  if (status === 'paused') return 'warning';
  if (status === 'completed') return 'info';
  return 'default';
};

const campaignSortTime = (campaign) => {
  const raw = campaign?.started_at || campaign?.created_at;
  const time = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
};

const campaignListPriority = (status) => {
  if (status === 'running') return 0;
  if (status === 'paused') return 1;
  if (status === 'draft') return 2;
  return 3; // completed and anything else
};

const sortCampaignsForList = (list = []) =>
  [...list].sort((a, b) => {
    const aIncomplete = ['running', 'paused', 'draft'].includes(a.status) ? 0 : 1;
    const bIncomplete = ['running', 'paused', 'draft'].includes(b.status) ? 0 : 1;
    if (aIncomplete !== bIncomplete) return aIncomplete - bIncomplete;
    const byStatus = campaignListPriority(a.status) - campaignListPriority(b.status);
    if (byStatus !== 0) return byStatus;
    return campaignSortTime(b) - campaignSortTime(a) || Number(b.id || 0) - Number(a.id || 0);
  });

const formatCampaignDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/** Desktop email canvas width — templates use width="600" tables. */
const EMAIL_PREVIEW_WIDTH = 600;
const NEW_TEMPLATE_VALUE = '__new_template__';

/**
 * Scales the 600px email canvas to fit narrow viewports so mobile preview
 * isn't clipped with a useless horizontal scrollbar.
 */
const EmailPreviewFrame = ({ html, title, height = 420 }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const update = () => {
      const width = node.clientWidth || EMAIL_PREVIEW_WIDTH;
      setScale(Math.min(1, width / EMAIL_PREVIEW_WIDTH));
    };
    update();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const frameHeight = Math.max(180, Math.ceil(height * scale));

  return (
    <Box
      ref={containerRef}
      data-testid="email-preview-frame"
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        bgcolor: '#fff',
        height: frameHeight,
        position: 'relative',
      }}
    >
      <Box
        component="iframe"
        title={title}
        srcDoc={html}
        sandbox=""
        sx={{
          border: 0,
          bgcolor: '#fff',
          width: EMAIL_PREVIEW_WIDTH,
          height,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          display: 'block',
          pointerEvents: 'auto',
        }}
      />
    </Box>
  );
};

const EmailBodyTabs = ({
  tab,
  onTabChange,
  bodyHtml,
  onBodyHtmlChange,
  preview,
  previewSample,
  previewLoading,
  onLoadPreview,
  disabled = false,
  minRows = 10,
  helperText,
  iframeHeight = 420,
}) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
      <Tabs
        value={tab}
        onChange={(_e, value) => onTabChange(value)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ ...documentSubTabsSx, mb: 2, maxWidth: '100%' }}
      >
        <Tab value="edit" label={t('adminCampaigns.bodyEditTab')} />
        <Tab value="preview" label={t('adminCampaigns.bodyPreviewTab')} />
      </Tabs>
      {tab === 'edit' ? (
        <TextField
          fullWidth
          multiline
          minRows={minRows}
          label={t('adminCampaigns.bodyHtml')}
          value={bodyHtml}
          onChange={(e) => onBodyHtmlChange(e.target.value)}
          disabled={disabled}
          helperText={helperText}
        />
      ) : (
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              onClick={() => onLoadPreview('named')}
              disabled={previewLoading}
              sx={previewSample === 'named' ? adminSolidBtnSx : adminOutlineBtnSx}
            >
              {t('adminCampaigns.previewSampleNamed')}
            </Button>
            <Button
              size="small"
              onClick={() => onLoadPreview('generic')}
              disabled={previewLoading}
              sx={previewSample === 'generic' ? adminSolidBtnSx : adminOutlineBtnSx}
            >
              {t('adminCampaigns.previewSampleGeneric')}
            </Button>
          </Stack>
          {previewLoading && !preview ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
            </Box>
          ) : preview ? (
            <>
              <Typography
                variant="body2"
                sx={{ mb: 1, color: DOCUMENT.ink3, wordBreak: 'break-word' }}
              >
                {t('adminCampaigns.emailPreviewMeta', {
                  company: preview.sampleLead?.company || '—',
                  contact: preview.sampleLead?.contact_name || t('adminCampaigns.genericContact'),
                  greeting: preview.greeting || '—',
                })}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: DOCUMENT.ink,
                  wordBreak: 'break-word',
                }}
              >
                {preview.subject}
              </Typography>
              <Box
                sx={{
                  border: `1px solid ${DOCUMENT.ruleHard}`,
                  bgcolor: DOCUMENT.ivory,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  opacity: previewLoading ? 0.6 : 1,
                }}
              >
                <EmailPreviewFrame
                  html={preview.html}
                  title={t('adminCampaigns.emailPreviewTitle')}
                  height={iframeHeight}
                />
              </Box>
            </>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

const AdminCampaigns = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const sortedCampaigns = useMemo(() => sortCampaignsForList(campaigns), [campaigns]);
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsTotalPages, setLeadsTotalPages] = useState(1);
  const [leadsLimit] = useState(100);
  const [leadQuery, setLeadQuery] = useState('');
  const [leadsStatusFilter, setLeadsStatusFilter] = useState('active');
  const [defaults, setDefaults] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateDraft, setTemplateDraft] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailPreviewSample, setEmailPreviewSample] = useState('named');
  const [createBodyTab, setCreateBodyTab] = useState('edit');
  const [createPreview, setCreatePreview] = useState(null);
  const [createPreviewSample, setCreatePreviewSample] = useState('named');
  const [createPreviewLoading, setCreatePreviewLoading] = useState(false);
  const [detailBodyTab, setDetailBodyTab] = useState('edit');
  const [detailPreview, setDetailPreview] = useState(null);
  const [detailPreviewSample, setDetailPreviewSample] = useState('named');
  const [detailPreviewLoading, setDetailPreviewLoading] = useState(false);
  const [engagementFollowUp, setEngagementFollowUp] = useState(null);
  const [engagementFollowUpLoading, setEngagementFollowUpLoading] = useState(false);
  const [engagementBodyTab, setEngagementBodyTab] = useState('edit');
  const [engagementPreview, setEngagementPreview] = useState(null);
  const [engagementPreviewSample, setEngagementPreviewSample] = useState('named');
  const [engagementPreviewLoading, setEngagementPreviewLoading] = useState(false);
  const [failedSendsDialog, setFailedSendsDialog] = useState(null);
  const [failedSendsLoading, setFailedSendsLoading] = useState(false);
  const [queuedSendsDialog, setQueuedSendsDialog] = useState(null);
  const [queuedSendsLoading, setQueuedSendsLoading] = useState(false);
  const [audienceFileInputKey, setAudienceFileInputKey] = useState(0);
  const [editingCampaignName, setEditingCampaignName] = useState(false);
  const [campaignNameDraft, setCampaignNameDraft] = useState('');
  const [createForm, setCreateForm] = useState({
    name: '',
    subject: '',
    bodyHtml: '',
    emailsPerDay: 50,
    templateId: '',
    templateName: '',
    audienceMode: 'all',
    audienceFile: null,
    selectedLeads: [],
    excludeEmailed: true,
    excludePartnerAccounts: true,
    priority: '',
    followUpCampaignId: '',
    followUpSegment: '',
  });
  const [audienceLeadOptions, setAudienceLeadOptions] = useState([]);
  const [audienceLeadTypes, setAudienceLeadTypes] = useState([]);
  const [audienceLeadCountries, setAudienceLeadCountries] = useState([]);
  const [audienceLeadPriorities, setAudienceLeadPriorities] = useState([]);
  const [audienceLeadTypeFilter, setAudienceLeadTypeFilter] = useState('');
  const [audienceLeadPriorityFilter, setAudienceLeadPriorityFilter] = useState('');
  const [audienceLeadCountryFilter, setAudienceLeadCountryFilter] = useState('');
  const [audienceLeadsLoading, setAudienceLeadsLoading] = useState(false);
  const [audienceLeadSearchInput, setAudienceLeadSearchInput] = useState('');
  const [audienceSelectAllLoading, setAudienceSelectAllLoading] = useState(false);
  const [audienceMatchTotal, setAudienceMatchTotal] = useState(0);
  const audienceLeadSearchRef = useRef('');
  const audienceLeadTypeRef = useRef('');
  const audienceLeadPriorityRef = useRef('');
  const audienceLeadCountryRef = useRef('');
  const audienceLeadSearchTimer = useRef(null);

  const openEngagementFollowUp = async (segment) => {
    if (!campaignDetail?.campaign?.id) return;
    setEngagementFollowUpLoading(true);
    setError('');
    try {
      const res = await api.get(
        `/admin/campaigns/campaigns/${campaignDetail.campaign.id}/engagement`,
        { params: { segment, limit: 500 } }
      );
      const contacts = res.data.engagement || [];
      setEngagementFollowUp({
        segment,
        sourceCampaignId: campaignDetail.campaign.id,
        sourceCampaignName: campaignDetail.campaign.name,
        contacts,
        selectedLeadIds: contacts.map((c) => c.lead_id),
        subject: res.data.template?.subject || '',
        bodyHtml: res.data.template?.body_html || '',
        templateName: res.data.template?.name || '',
        emailsPerDay: 40,
      });
      setEngagementBodyTab('edit');
      setEngagementPreview(null);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.loadError'));
    } finally {
      setEngagementFollowUpLoading(false);
    }
  };

  const loadEngagementPreview = async (sample = engagementPreviewSample) => {
    if (!engagementFollowUp) return;
    setEngagementPreviewLoading(true);
    setEngagementPreviewSample(sample);
    try {
      const res = await api.post('/admin/campaigns/templates/preview', {
        subject: engagementFollowUp.subject,
        bodyHtml: engagementFollowUp.bodyHtml,
        sample,
      });
      setEngagementPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.loadError'));
    } finally {
      setEngagementPreviewLoading(false);
    }
  };

  const sendEngagementFollowUp = async () => {
    if (!engagementFollowUp?.sourceCampaignId) return;
    if (!engagementFollowUp.selectedLeadIds?.length) {
      setError(t('adminCampaigns.selectAtLeastOneContact'));
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      const res = await api.post(
        `/admin/campaigns/campaigns/${engagementFollowUp.sourceCampaignId}/engagement-followup`,
        {
          segment: engagementFollowUp.segment,
          subject: engagementFollowUp.subject,
          bodyHtml: engagementFollowUp.bodyHtml,
          emailsPerDay: engagementFollowUp.emailsPerDay,
          leadIds: engagementFollowUp.selectedLeadIds,
          sendNow: true,
          sendLimit: 25,
        }
      );
      setSuccess(
        t('adminCampaigns.engagementFollowUpQueued', {
          enqueued: res.data?.enqueued || 0,
        })
      );
      setEngagementFollowUp(null);
      await loadOverview();
      if (res.data?.campaign?.id) {
        setSelectedCampaignId(res.data.campaign.id);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.sendError'));
    } finally {
      setActionLoading(false);
    }
  };

  const sortTemplates = (list) =>
    [...list].sort((a, b) => {
      if (Boolean(b.is_builtin) !== Boolean(a.is_builtin)) {
        return Number(b.is_builtin) - Number(a.is_builtin);
      }
      return String(a.name).localeCompare(String(b.name));
    });

  const applyTemplateToCreateForm = (tpl) => {
    if (!tpl) return;
    setCreateForm((prev) => ({
      ...prev,
      templateId: String(tpl.id),
      templateName: '',
      subject: tpl.subject,
      bodyHtml: tpl.body_html,
      excludeEmailed: tpl.slug === 'follow-up-no-response' ? false : prev.excludeEmailed,
      followUpSegment: tpl.slug === 'follow-up-no-response' ? 'no_visit' : prev.followUpSegment,
    }));
  };

  const startNewTemplateInCreateForm = () => {
    setCreateForm((prev) => ({
      ...prev,
      templateId: NEW_TEMPLATE_VALUE,
      templateName: '',
      subject: '',
      bodyHtml: '',
    }));
    setCreateBodyTab('edit');
    setCreatePreview(null);
  };

  const startBlankTemplateDraft = () => {
    setSelectedTemplateId(null);
    setTemplateDraft({
      id: null,
      slug: '',
      name: '',
      description: '',
      subject: '',
      body_html: '',
      is_builtin: false,
    });
  };

  const persistCreatedTemplate = (created) => {
    setTemplates((prev) => sortTemplates([...prev.filter((tpl) => tpl.id !== created.id), created]));
    setSelectedTemplateId(created.id);
    setTemplateDraft(created);
    return created;
  };

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [campaignsRes, leadsRes, defaultsRes] = await Promise.all([
        api.get('/admin/campaigns/campaigns'),
        api.get('/admin/campaigns/leads', {
          params: { limit: leadsLimit, page: 1, status: 'active' },
        }),
        api.get('/admin/campaigns/defaults'),
      ]);
      setCampaigns(campaignsRes.data.campaigns || []);
      setLeads(leadsRes.data.leads || []);
      setLeadsTotal(leadsRes.data.total || 0);
      setLeadsPage(leadsRes.data.page || 1);
      setLeadsTotalPages(leadsRes.data.totalPages || 1);
      setLeadsStatusFilter('active');
      setLeadQuery('');
      setDefaults(defaultsRes.data);
      const tplList = defaultsRes.data.templates || [];
      setTemplates(tplList);
      setCreateForm((prev) => {
        const initial =
          tplList.find((t) => t.id === defaultsRes.data.defaultTemplateId) ||
          tplList.find((t) => t.slug === 'initial-outreach') ||
          tplList[0];
        return {
          ...prev,
          subject: prev.subject || initial?.subject || defaultsRes.data.subject,
          bodyHtml: prev.bodyHtml || initial?.body_html || defaultsRes.data.bodyHtml,
          emailsPerDay: defaultsRes.data.emailsPerDay || 50,
          templateId: prev.templateId || (initial ? String(initial.id) : ''),
        };
      });
      setSelectedTemplateId((currentId) => currentId || tplList[0]?.id || null);
      setTemplateDraft((current) => current || tplList[0] || null);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t, leadsLimit]);

  const loadCampaignDetail = useCallback(async (id) => {
    if (!id) return;
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/campaigns/campaigns/${id}`);
      setCampaignDetail(res.data);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.loadError'));
    } finally {
      setDetailLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (selectedCampaignId) loadCampaignDetail(selectedCampaignId);
  }, [selectedCampaignId, loadCampaignDetail]);

  const openQueuedSendsDialog = async () => {
    if (!campaignDetail?.campaign?.id) return;
    if (!Number(campaignDetail.sends?.queued || 0)) return;
    setQueuedSendsLoading(true);
    setError('');
    try {
      const res = await api.get(
        `/admin/campaigns/campaigns/${campaignDetail.campaign.id}/queue`
      );
      setQueuedSendsDialog({
        campaignId: campaignDetail.campaign.id,
        campaignName: campaignDetail.campaign.name,
        queue: res.data.queue || [],
        total: res.data.total || 0,
      });
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.loadError'));
    } finally {
      setQueuedSendsLoading(false);
    }
  };

  const openFailedSendsDialog = async () => {
    if (!campaignDetail?.campaign?.id) return;
    if (!Number(campaignDetail.sends?.failed || 0)) return;
    setFailedSendsLoading(true);
    setError('');
    try {
      const res = await api.get(
        `/admin/campaigns/campaigns/${campaignDetail.campaign.id}/failures`
      );
      const failures = res.data.failures || [];
      setFailedSendsDialog({
        campaignId: campaignDetail.campaign.id,
        campaignName: campaignDetail.campaign.name,
        failures,
        selectedSendIds: failures.filter((f) => f.requeueable).map((f) => f.id),
        requeueableCount: res.data.requeueableCount || 0,
      });
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.loadError'));
    } finally {
      setFailedSendsLoading(false);
    }
  };

  const requeueSelectedFailures = async () => {
    if (!failedSendsDialog?.campaignId) return;
    if (!failedSendsDialog.selectedSendIds?.length) {
      setError(t('adminCampaigns.failedRequeueNoneSelected'));
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      const campaignId = failedSendsDialog.campaignId;
      const res = await api.post(
        `/admin/campaigns/campaigns/${campaignId}/failures/requeue`,
        { sendIds: failedSendsDialog.selectedSendIds }
      );
      setSuccess(
        t('adminCampaigns.failedRequeueSuccess', {
          requeued: res.data.requeued || 0,
          skipped: res.data.skipped || 0,
        })
      );
      setFailedSendsDialog(null);
      await loadOverview();
      await loadCampaignDetail(campaignId);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.failedRequeueError'));
    } finally {
      setActionLoading(false);
    }
  };

  const downloadLeadsTemplate = async () => {
    setActionLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/campaigns/uploads/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dejavunomad-leads-template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.templateDownloadError'));
    } finally {
      setActionLoading(false);
    }
  };

  const closeUploadPreview = () => {
    setUploadPreviewOpen(false);
    setUploadPreview(null);
    setPendingUploadFile(null);
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/campaigns/uploads/preview', formData);
      setPendingUploadFile(file);
      setUploadPreview(res.data);
      setUploadPreviewOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.uploadPreviewError'));
    } finally {
      setActionLoading(false);
    }
  };

  const confirmUpload = async () => {
    if (!pendingUploadFile) return;
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const formData = new FormData();
      formData.append('file', pendingUploadFile);
      const res = await api.post('/admin/campaigns/uploads', formData);
      closeUploadPreview();
      setSuccess(
        t('adminCampaigns.uploadSuccess', {
          newCount: res.data.newCount,
          duplicateCount: res.data.duplicateCount,
          invalidCount: res.data.invalidCount,
        })
      );
      await loadOverview();
      setTab(1);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.uploadError'));
    } finally {
      setActionLoading(false);
    }
  };

  const searchLeads = async (page = 1, overrides = {}) => {
    setActionLoading(true);
    try {
      const q = overrides.q !== undefined ? overrides.q : leadQuery;
      const status =
        overrides.status !== undefined ? overrides.status : leadsStatusFilter;
      const res = await api.get('/admin/campaigns/leads', {
        params: {
          q: q || undefined,
          status: status || 'active',
          page,
          limit: leadsLimit,
        },
      });
      setLeads(res.data.leads || []);
      setLeadsTotal(res.data.total || 0);
      setLeadsPage(res.data.page || page);
      setLeadsTotalPages(res.data.totalPages || 1);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.loadError'));
    } finally {
      setActionLoading(false);
    }
  };

  const applyAudienceLeadFacetResponse = useCallback((data) => {
    setAudienceLeadOptions(data.leads || []);
    setAudienceLeadTypes(data.types || []);
    setAudienceLeadCountries(data.countries || []);
    setAudienceLeadPriorities(data.priorities || []);
    setAudienceMatchTotal(Number(data.total || 0));
  }, []);

  const loadAudienceLeadOptions = useCallback(async (q = audienceLeadSearchRef.current) => {
    setAudienceLeadsLoading(true);
    try {
      const res = await api.get('/admin/campaigns/leads', {
        params: {
          q: q || undefined,
          type: audienceLeadTypeRef.current || undefined,
          priority: audienceLeadPriorityRef.current || undefined,
          country: audienceLeadCountryRef.current || undefined,
          status: 'active',
          page: 1,
          limit: 100,
        },
      });
      applyAudienceLeadFacetResponse(res.data);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.loadError'));
    } finally {
      setAudienceLeadsLoading(false);
    }
  }, [applyAudienceLeadFacetResponse, t]);

  const scheduleAudienceLeadSearch = useCallback((q) => {
    audienceLeadSearchRef.current = q;
    if (audienceLeadSearchTimer.current) {
      clearTimeout(audienceLeadSearchTimer.current);
    }
    audienceLeadSearchTimer.current = setTimeout(() => {
      loadAudienceLeadOptions(audienceLeadSearchRef.current);
    }, 250);
  }, [loadAudienceLeadOptions]);

  const changeAudienceLeadTypeFilter = useCallback((nextType) => {
    const value = String(nextType || '');
    setAudienceLeadTypeFilter(value);
    audienceLeadTypeRef.current = value;
    loadAudienceLeadOptions(audienceLeadSearchRef.current);
  }, [loadAudienceLeadOptions]);

  const changeAudienceLeadPriorityFilter = useCallback((nextPriority) => {
    const value = String(nextPriority || '');
    setAudienceLeadPriorityFilter(value);
    audienceLeadPriorityRef.current = value;
    loadAudienceLeadOptions(audienceLeadSearchRef.current);
  }, [loadAudienceLeadOptions]);

  const changeAudienceLeadCountryFilter = useCallback((nextCountry) => {
    const value = String(nextCountry || '');
    setAudienceLeadCountryFilter(value);
    audienceLeadCountryRef.current = value;
    loadAudienceLeadOptions(audienceLeadSearchRef.current);
  }, [loadAudienceLeadOptions]);

  const selectAllMatchingAudienceLeads = useCallback(async () => {
    setAudienceSelectAllLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/campaigns/leads', {
        params: {
          q: audienceLeadSearchRef.current || undefined,
          type: audienceLeadTypeRef.current || undefined,
          priority: audienceLeadPriorityRef.current || undefined,
          country: audienceLeadCountryRef.current || undefined,
          status: 'active',
          page: 1,
          limit: 500,
        },
      });
      const rows = res.data.leads || [];
      applyAudienceLeadFacetResponse(res.data);
      setCreateForm((prev) => {
        const byId = new Map();
        [...(prev.selectedLeads || []), ...rows].forEach((lead) => {
          if (lead?.id != null) byId.set(Number(lead.id), lead);
        });
        return { ...prev, selectedLeads: [...byId.values()] };
      });
      if (Number(res.data.total || 0) > rows.length) {
        setSuccess(t('adminCampaigns.audienceSelectAllPartial', {
          selected: rows.length,
          total: res.data.total,
        }));
      }
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.loadError'));
    } finally {
      setAudienceSelectAllLoading(false);
    }
  }, [applyAudienceLeadFacetResponse, t]);

  const resetAudienceLeadFilters = useCallback(() => {
    setAudienceLeadTypeFilter('');
    setAudienceLeadPriorityFilter('');
    setAudienceLeadCountryFilter('');
    setAudienceLeadSearchInput('');
    setAudienceLeadOptions([]);
    setAudienceLeadTypes([]);
    setAudienceLeadCountries([]);
    setAudienceLeadPriorities([]);
    setAudienceMatchTotal(0);
    audienceLeadTypeRef.current = '';
    audienceLeadPriorityRef.current = '';
    audienceLeadCountryRef.current = '';
    audienceLeadSearchRef.current = '';
  }, []);

  const clearAudienceLeadSelection = useCallback(() => {
    setCreateForm((prev) => ({ ...prev, selectedLeads: [] }));
  }, []);

  const audienceSelectAllState = useMemo(() => {
    const selected = new Set((createForm.selectedLeads || []).map((lead) => Number(lead.id)));
    if (!audienceLeadOptions.length) {
      return { all: false, some: (createForm.selectedLeads || []).length > 0 };
    }
    let selectedVisible = 0;
    audienceLeadOptions.forEach((lead) => {
      if (selected.has(Number(lead.id))) selectedVisible += 1;
    });
    return {
      all: selectedVisible === audienceLeadOptions.length,
      some: selectedVisible > 0 && selectedVisible < audienceLeadOptions.length,
    };
  }, [audienceLeadOptions, createForm.selectedLeads]);

  const toggleAudienceSelectAll = useCallback(() => {
    if (audienceSelectAllState.all) clearAudienceLeadSelection();
    else selectAllMatchingAudienceLeads();
  }, [audienceSelectAllState.all, clearAudienceLeadSelection, selectAllMatchingAudienceLeads]);

  const toggleAudienceLead = useCallback((lead) => {
    setCreateForm((prev) => {
      const selected = prev.selectedLeads || [];
      const id = Number(lead.id);
      const exists = selected.some((row) => Number(row.id) === id);
      return {
        ...prev,
        selectedLeads: exists
          ? selected.filter((row) => Number(row.id) !== id)
          : [...selected, lead],
      };
    });
  }, []);

  const audienceSelectedIdSet = useMemo(
    () => new Set((createForm.selectedLeads || []).map((lead) => Number(lead.id))),
    [createForm.selectedLeads]
  );

  const audienceVisibleSelectedCount = useMemo(() => {
    const visibleIds = new Set(audienceLeadOptions.map((lead) => Number(lead.id)));
    return (createForm.selectedLeads || []).filter((lead) => visibleIds.has(Number(lead.id))).length;
  }, [audienceLeadOptions, createForm.selectedLeads]);

  useEffect(() => () => {
    if (audienceLeadSearchTimer.current) {
      clearTimeout(audienceLeadSearchTimer.current);
    }
  }, []);

  const exportLeadsBook = async () => {
    setActionLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/campaigns/leads/export', {
        params: {
          q: leadQuery || undefined,
          status: leadsStatusFilter || 'active',
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `dejavunomad-outreach-leads-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      setSuccess(t('adminCampaigns.exportLeadsSuccess', { count: leadsTotal }));
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.exportLeadsError'));
    } finally {
      setActionLoading(false);
    }
  };

  const setLeadInactiveState = async (lead, inactive) => {
    setActionLoading(true);
    setError('');
    try {
      await api.patch(`/admin/campaigns/leads/${lead.id}`, { inactive });
      setSuccess(
        inactive
          ? t('adminCampaigns.leadMarkedInactive', { email: lead.email })
          : t('adminCampaigns.leadReactivated', { email: lead.email })
      );
      await searchLeads(leadsPage);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.leadUpdateError'));
    } finally {
      setActionLoading(false);
    }
  };

  const deleteLeadRow = async (lead) => {
    if (
      !window.confirm(
        t('adminCampaigns.deleteLeadConfirm', { email: lead.email, company: lead.company || '' })
      )
    ) {
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      await api.delete(`/admin/campaigns/leads/${lead.id}`);
      setSuccess(t('adminCampaigns.leadDeleted', { email: lead.email }));
      await searchLeads(leadsPage);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.leadDeleteError'));
    } finally {
      setActionLoading(false);
    }
  };

  const createCampaign = async () => {
    if (createForm.audienceMode === 'file' && !createForm.audienceFile) {
      setError(t('adminCampaigns.audienceFileRequired'));
      return;
    }
    if (createForm.audienceMode === 'select' && !(createForm.selectedLeads || []).length) {
      setError(t('adminCampaigns.audienceSelectRequired'));
      return;
    }
    const creatingNewTemplate = createForm.templateId === NEW_TEMPLATE_VALUE;
    const newTemplateName = String(createForm.templateName || '').trim();
    if (creatingNewTemplate && !newTemplateName) {
      setError(t('adminCampaigns.templateNameRequired'));
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      let templateId = createForm.templateId
        && createForm.templateId !== NEW_TEMPLATE_VALUE
        ? Number(createForm.templateId)
        : undefined;

      if (creatingNewTemplate) {
        const tplRes = await api.post('/admin/campaigns/templates', {
          name: newTemplateName,
          subject: createForm.subject,
          bodyHtml: createForm.bodyHtml,
        });
        const created = persistCreatedTemplate(tplRes.data.template);
        templateId = created.id;
        applyTemplateToCreateForm(created);
      }

      let uploadId;
      if (createForm.audienceMode === 'file' && createForm.audienceFile) {
        const formData = new FormData();
        formData.append('file', createForm.audienceFile);
        const uploadRes = await api.post('/admin/campaigns/uploads', formData);
        uploadId = uploadRes.data.uploadId;
      }

      const audienceFilter = {
        excludeEmailed: createForm.excludeEmailed,
        excludePartnerAccounts: createForm.excludePartnerAccounts,
      };
      if (uploadId) audienceFilter.uploadId = Number(uploadId);
      if (createForm.audienceMode === 'select') {
        audienceFilter.leadIds = (createForm.selectedLeads || [])
          .map((lead) => Number(lead.id))
          .filter((id) => id > 0);
      }
      if (createForm.priority) audienceFilter.priority = createForm.priority;
      if (createForm.followUpCampaignId) {
        audienceFilter.followUpCampaignId = Number(createForm.followUpCampaignId);
        audienceFilter.followUpSegment = createForm.followUpSegment || 'no_visit';
        audienceFilter.excludeEmailed = false;
      }

      const res = await api.post('/admin/campaigns/campaigns', {
        name: createForm.name,
        subject: createForm.subject,
        bodyHtml: createForm.bodyHtml,
        emailsPerDay: Number(createForm.emailsPerDay) || 50,
        templateId,
        audienceFilter,
      });
      setCreateOpen(false);
      setCreateBodyTab('edit');
      setCreatePreview(null);
      setCreatePreviewSample('named');
      setCreateForm((prev) => ({
        ...prev,
        audienceMode: 'all',
        audienceFile: null,
        selectedLeads: [],
        templateName: '',
      }));
      resetAudienceLeadFilters();
      const enqueued = Number(res.data?.enqueued || 0);
      const selectedCount = audienceFilter.leadIds?.length || 0;
      setSuccess(
        creatingNewTemplate
          ? t('adminCampaigns.createSuccessWithNewTemplateQueued', {
              template: newTemplateName,
              count: enqueued,
            })
          : uploadId
            ? t('adminCampaigns.createSuccessWithImportQueued', { count: enqueued })
            : selectedCount
              ? t('adminCampaigns.createSuccessWithSelectedQueued', { count: enqueued })
              : t('adminCampaigns.createSuccessQueued', { count: enqueued })
      );
      await loadOverview();
      setSelectedCampaignId(res.data.campaign.id);
      setDetailBodyTab('edit');
      setDetailPreview(null);
      setTab(0);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.createError'));
    } finally {
      setActionLoading(false);
    }
  };

  const setCampaignStatus = async (id, status) => {
    setActionLoading(true);
    setError('');
    try {
      await api.patch(`/admin/campaigns/campaigns/${id}`, { status });
      setSuccess(t('adminCampaigns.statusUpdated', { status }));
      await loadOverview();
      if (selectedCampaignId === id) await loadCampaignDetail(id);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.updateError'));
    } finally {
      setActionLoading(false);
    }
  };

  const sendNow = async (id, limit = 5) => {
    setActionLoading(true);
    setError('');
    try {
      const res = await api.post(`/admin/campaigns/campaigns/${id}/send-now`, { limit });
      setSuccess(
        t('adminCampaigns.sendNowResult', {
          sent: res.data.sent,
          failed: res.data.failed,
          reason: res.data.reason || '',
        })
      );
      await loadOverview();
      await loadCampaignDetail(id);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.sendError'));
    } finally {
      setActionLoading(false);
    }
  };

  const testSend = async (id) => {
    setActionLoading(true);
    setError('');
    try {
      const res = await api.post(`/admin/campaigns/campaigns/${id}/test-send`, {});
      setSuccess(t('adminCampaigns.testSent', { to: res.data.to }));
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.sendError'));
    } finally {
      setActionLoading(false);
    }
  };

  const saveTemplateDraft = async () => {
    if (!templateDraft) return;
    const name = String(templateDraft.name || '').trim();
    if (!name) {
      setError(t('adminCampaigns.templateNameRequired'));
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      if (!templateDraft.id) {
        const res = await api.post('/admin/campaigns/templates', {
          name,
          description: templateDraft.description,
          subject: templateDraft.subject,
          bodyHtml: templateDraft.body_html,
        });
        persistCreatedTemplate(res.data.template);
        setSuccess(t('adminCampaigns.templateCreated', { name }));
      } else {
        const res = await api.patch(`/admin/campaigns/templates/${templateDraft.id}`, {
          name,
          description: templateDraft.description,
          subject: templateDraft.subject,
          bodyHtml: templateDraft.body_html,
        });
        persistCreatedTemplate(res.data.template);
        setSuccess(t('adminCampaigns.templateSaved'));
      }
      await loadOverview();
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.updateError'));
    } finally {
      setActionLoading(false);
    }
  };

  const previewTemplateDraft = async (sample = emailPreviewSample) => {
    if (!templateDraft) return;
    setActionLoading(true);
    setError('');
    try {
      const res = await api.post('/admin/campaigns/templates/preview', {
        subject: templateDraft.subject,
        bodyHtml: templateDraft.body_html,
        sample,
      });
      setEmailPreviewSample(sample);
      setEmailPreview(res.data);
      setEmailPreviewOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.emailPreviewError'));
    } finally {
      setActionLoading(false);
    }
  };

  const loadCreateEmailPreview = async (sample = createPreviewSample) => {
    setCreatePreviewLoading(true);
    setError('');
    try {
      const res = await api.post('/admin/campaigns/templates/preview', {
        subject: createForm.subject,
        bodyHtml: createForm.bodyHtml,
        sample,
      });
      setCreatePreviewSample(sample);
      setCreatePreview(res.data);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.emailPreviewError'));
      setCreateBodyTab('edit');
    } finally {
      setCreatePreviewLoading(false);
    }
  };

  const loadDetailEmailPreview = async (sample = detailPreviewSample) => {
    if (!campaignDetail?.campaign) return;
    setDetailPreviewLoading(true);
    setError('');
    try {
      const res = await api.post('/admin/campaigns/templates/preview', {
        subject: campaignDetail.campaign.subject,
        bodyHtml: campaignDetail.campaign.body_html,
        sample,
      });
      setDetailPreviewSample(sample);
      setDetailPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.emailPreviewError'));
      setDetailBodyTab('edit');
    } finally {
      setDetailPreviewLoading(false);
    }
  };

  const closeCreateDialog = () => {
    setCreateOpen(false);
    setCreateBodyTab('edit');
    setCreatePreview(null);
    setCreatePreviewSample('named');
    setCreateForm((prev) => ({
      ...prev,
      audienceMode: 'all',
      audienceFile: null,
      selectedLeads: [],
      templateName: '',
    }));
    resetAudienceLeadFilters();
  };

  const resetTemplateDraft = async () => {
    if (!templateDraft?.id) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/campaigns/templates/${templateDraft.id}/reset`);
      setTemplateDraft(res.data.template);
      setSuccess(t('adminCampaigns.templateReset'));
      await loadOverview();
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.updateError'));
    } finally {
      setActionLoading(false);
    }
  };

  const openCreateWithTemplate = (tpl) => {
    applyTemplateToCreateForm(tpl);
    setCreateOpen(true);
  };

  const saveCampaignEdits = async () => {
    if (!campaignDetail?.campaign) return;
    const c = campaignDetail.campaign;
    setActionLoading(true);
    try {
      await api.patch(`/admin/campaigns/campaigns/${c.id}`, {
        name: c.name,
        subject: c.subject,
        bodyHtml: c.body_html,
        emailsPerDay: c.emails_per_day,
      });
      setSuccess(t('adminCampaigns.saved'));
      await loadCampaignDetail(c.id);
      await loadOverview();
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.updateError'));
    } finally {
      setActionLoading(false);
    }
  };

  const replaceCampaignAudienceFile = async (file) => {
    if (!campaignDetail?.campaign?.id || !file) return;
    setActionLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(
        `/admin/campaigns/campaigns/${campaignDetail.campaign.id}/audience`,
        formData
      );
      setSuccess(
        t('adminCampaigns.audienceReplaced', {
          newCount: res.data?.import?.newCount || 0,
          duplicateCount: res.data?.import?.duplicateCount || 0,
          enqueued: res.data?.enqueued || 0,
        })
      );
      setAudienceFileInputKey((k) => k + 1);
      await loadCampaignDetail(campaignDetail.campaign.id);
      await loadOverview();
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.audienceReplaceError'));
    } finally {
      setActionLoading(false);
    }
  };

  const rebuildCampaignQueueNow = async () => {
    if (!campaignDetail?.campaign?.id) return;
    setActionLoading(true);
    setError('');
    try {
      const res = await api.post(
        `/admin/campaigns/campaigns/${campaignDetail.campaign.id}/enqueue`,
        { rebuild: true }
      );
      setSuccess(
        t('adminCampaigns.queueRebuilt', {
          enqueued: res.data?.enqueued || 0,
          skipped: res.data?.skipped || 0,
        })
      );
      await loadCampaignDetail(campaignDetail.campaign.id);
      await loadOverview();
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.queueRebuildError'));
    } finally {
      setActionLoading(false);
    }
  };

  const beginEditCampaignName = () => {
    if (!campaignDetail?.campaign) return;
    setCampaignNameDraft(campaignDetail.campaign.name || '');
    setEditingCampaignName(true);
  };

  const cancelEditCampaignName = () => {
    setEditingCampaignName(false);
    setCampaignNameDraft('');
  };

  const saveCampaignName = async () => {
    if (!campaignDetail?.campaign) return;
    const nextName = String(campaignNameDraft || '').trim();
    if (!nextName) {
      setError(t('adminCampaigns.nameRequired'));
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      await api.patch(`/admin/campaigns/campaigns/${campaignDetail.campaign.id}`, {
        name: nextName,
      });
      setCampaignDetail((prev) => ({
        ...prev,
        campaign: { ...prev.campaign, name: nextName },
      }));
      setEditingCampaignName(false);
      setSuccess(t('adminCampaigns.saved'));
      await loadOverview();
    } catch (err) {
      setError(err.response?.data?.error || t('adminCampaigns.updateError'));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminChrome title={t('adminCampaigns.title')} subtitle={t('adminCampaigns.pageSubtitle')}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
        </Box>
      </AdminChrome>
    );
  }

  const headerActions = (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Button startIcon={<RefreshIcon />} onClick={loadOverview} disabled={actionLoading} sx={adminOutlineBtnSx}>
        {t('adminCampaigns.refresh')}
      </Button>
      <Button startIcon={<CampaignIcon />} onClick={() => setCreateOpen(true)} sx={adminSolidBtnSx}>
        {t('adminCampaigns.newCampaign')}
      </Button>
      <Button startIcon={<DownloadIcon />} onClick={downloadLeadsTemplate} disabled={actionLoading} sx={adminOutlineBtnSx}>
        {t('adminCampaigns.downloadTemplate')}
      </Button>
      <Button component="label" startIcon={<UploadIcon />} disabled={actionLoading} sx={adminOutlineBtnSx}>
        {t('adminCampaigns.uploadLeads')}
        <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} />
      </Button>
    </Stack>
  );

  return (
    <AdminChrome
      title={t('adminCampaigns.title')}
      subtitle={t('adminCampaigns.pageSubtitle')}
      actions={headerActions}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{ ...documentSubTabsSx, mb: 3 }}
      >
        <Tab label={t('adminCampaigns.tabCampaigns')} disableRipple />
        <Tab label={t('adminCampaigns.tabLeads', { count: leadsTotal })} disableRipple />
        <Tab label={t('adminCampaigns.tabTemplates')} disableRipple />
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={selectedCampaignId ? 5 : 12}>
            <Card sx={adminSurfaceCardSx}>
              <CardContent>
                <Typography sx={{ mb: 2, fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.25rem', color: DOCUMENT.ink }}>
                  {t('adminCampaigns.campaigns')}
                </Typography>
                {campaigns.length === 0 ? (
                  <Typography color="text.secondary">{t('adminCampaigns.noCampaigns')}</Typography>
                ) : (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 420 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '46%' }}>{t('adminCampaigns.name')}</TableCell>
                          <TableCell sx={{ width: '22%' }}>{t('adminCampaigns.status')}</TableCell>
                          <TableCell align="right" sx={{ width: '16%' }}>{t('adminCampaigns.sent')}</TableCell>
                          <TableCell align="right" sx={{ width: '16%' }}>{t('adminCampaigns.failed')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedCampaigns.map((c) => (
                          <TableRow
                            key={c.id}
                            hover
                            selected={selectedCampaignId === c.id}
                            onClick={() => {
                              setSelectedCampaignId(c.id);
                              setEditingCampaignName(false);
                              setDetailBodyTab('edit');
                              setDetailPreview(null);
                              setDetailPreviewSample('named');
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell sx={{ maxWidth: 0 }}>
                              <Typography
                                fontWeight={600}
                                noWrap
                                title={c.name}
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {c.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap display="block">
                                {c.emails_per_day}/day
                                {' · '}
                                {formatCampaignDate(c.started_at || c.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={c.status} color={statusColor(c.status)} />
                            </TableCell>
                            <TableCell align="right">{c.sent_rows ?? c.sent_count}</TableCell>
                            <TableCell align="right">{c.failed_rows ?? c.failed_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {selectedCampaignId && (
            <Grid item xs={12} md={7}>
              <Card sx={adminSurfaceCardSx}>
                <CardContent>
                  {detailLoading || !campaignDetail ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                          {editingCampaignName ? (
                            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.5 }}>
                              <TextField
                                fullWidth
                                size="small"
                                autoFocus
                                label={t('adminCampaigns.name')}
                                value={campaignNameDraft}
                                onChange={(e) => setCampaignNameDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    saveCampaignName();
                                  }
                                  if (e.key === 'Escape') cancelEditCampaignName();
                                }}
                                disabled={actionLoading}
                              />
                              <IconButton
                                aria-label={t('adminCampaigns.save')}
                                onClick={saveCampaignName}
                                disabled={actionLoading}
                                size="small"
                                sx={{ mt: 0.5 }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                aria-label={t('adminCampaigns.cancel')}
                                onClick={cancelEditCampaignName}
                                disabled={actionLoading}
                                size="small"
                                sx={{ mt: 0.5 }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                              <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.35rem', color: DOCUMENT.ink }}>
                                {campaignDetail.campaign.name}
                              </Typography>
                              <IconButton
                                aria-label={t('adminCampaigns.renameCampaign')}
                                size="small"
                                onClick={beginEditCampaignName}
                                disabled={actionLoading}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )}
                          <Chip
                            size="small"
                            label={campaignDetail.campaign.status}
                            color={statusColor(campaignDetail.campaign.status)}
                            sx={{ mt: 0.5, borderRadius: 0 }}
                          />
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button size="small" startIcon={<EmailIcon />} onClick={() => testSend(campaignDetail.campaign.id)} disabled={actionLoading} sx={adminOutlineBtnSx}>
                            {t('adminCampaigns.testSend')}
                          </Button>
                          <Button size="small" startIcon={<SendIcon />} onClick={() => sendNow(campaignDetail.campaign.id, 5)} disabled={actionLoading} sx={adminOutlineBtnSx}>
                            {t('adminCampaigns.sendNow')}
                          </Button>
                          {campaignDetail.campaign.status !== 'running' && campaignDetail.campaign.status !== 'completed' && (
                            <Button
                              size="small"
                              startIcon={<PlayIcon />}
                              onClick={() => setCampaignStatus(campaignDetail.campaign.id, 'running')}
                              disabled={actionLoading}
                              sx={adminSolidBtnSx}
                            >
                              {t('adminCampaigns.start')}
                            </Button>
                          )}
                          {campaignDetail.campaign.status === 'running' && (
                            <Button
                              size="small"
                              startIcon={<PauseIcon />}
                              onClick={() => setCampaignStatus(campaignDetail.campaign.id, 'paused')}
                              disabled={actionLoading}
                              sx={adminOutlineBtnSx}
                            >
                              {t('adminCampaigns.pause')}
                            </Button>
                          )}
                          {campaignDetail.campaign.status === 'paused' && (
                            <Button
                              size="small"
                              startIcon={<PlayIcon />}
                              onClick={() => setCampaignStatus(campaignDetail.campaign.id, 'running')}
                              disabled={actionLoading}
                              sx={adminSolidBtnSx}
                            >
                              {t('adminCampaigns.resume')}
                            </Button>
                          )}
                          <Button
                            size="small"
                            sx={adminOutlineBtnSx}
                            onClick={async () => {
                              try {
                                const res = await api.get(
                                  `/admin/campaigns/campaigns/${campaignDetail.campaign.id}/export`,
                                  { responseType: 'blob' }
                                );
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `campaign-${campaignDetail.campaign.id}-sends.csv`;
                                a.click();
                                window.URL.revokeObjectURL(url);
                              } catch (err) {
                                setError(err.response?.data?.error || t('adminCampaigns.loadError'));
                              }
                            }}
                          >
                            {t('adminCampaigns.export')}
                          </Button>
                        </Stack>
                      </Box>

                      {(() => {
                        const sent = Number(campaignDetail.sends?.sent || 0);
                        const failed = Number(campaignDetail.sends?.failed || 0);
                        const total = sent + failed;
                        const failRate = total > 0 ? failed / total : 0;
                        const likelyAutoPaused =
                          campaignDetail.campaign.status === 'paused' && total >= 20 && failRate >= 0.05;
                        if (!likelyAutoPaused) return null;
                        return (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            {t('adminCampaigns.autoPausedHighFailureRate', {
                              failed,
                              total,
                              rate: Math.round(failRate * 100),
                            })}
                          </Alert>
                        );
                      })()}

                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={4}>
                          <StatBox label={t('adminCampaigns.sent')} value={campaignDetail.sends.sent} color="#2e7d32" />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <StatBox
                            label={t('adminCampaigns.failed')}
                            value={campaignDetail.sends.failed}
                            color="#c62828"
                            hint={
                              campaignDetail.sends.failed > 0
                                ? t('adminCampaigns.clickToReviewFailures')
                                : null
                            }
                            onClick={
                              campaignDetail.sends.failed > 0 ? openFailedSendsDialog : null
                            }
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <StatBox
                            label={t('adminCampaigns.queued')}
                            value={campaignDetail.sends.queued}
                            hint={
                              campaignDetail.sends.queued > 0
                                ? t('adminCampaigns.clickToReviewQueue')
                                : null
                            }
                            onClick={
                              campaignDetail.sends.queued > 0 ? openQueuedSendsDialog : null
                            }
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <StatBox
                            label={t('adminCampaigns.siteVisits')}
                            value={campaignDetail.engagement.uniqueSiteVisits}
                            color="#1565c0"
                            hint={t('adminCampaigns.clickToFollowUp')}
                            onClick={() => openEngagementFollowUp('site_visits')}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <StatBox
                            label={t('adminCampaigns.demoClicks')}
                            value={campaignDetail.engagement.demoClicks}
                            color="#6a1b9a"
                            hint={t('adminCampaigns.clickToFollowUp')}
                            onClick={() => openEngagementFollowUp('demo_clicks')}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <StatBox
                            label={t('adminCampaigns.bookings')}
                            value={campaignDetail.engagement.bookings}
                            color="#e65100"
                            hint={t('adminCampaigns.clickToFollowUp')}
                            onClick={() => openEngagementFollowUp('bookings')}
                          />
                        </Grid>
                      </Grid>

                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {t('adminCampaigns.engagementList')}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button
                              size="small"
                              sx={adminOutlineBtnSx}
                              disabled={actionLoading}
                              onClick={async () => {
                                try {
                                  setActionLoading(true);
                                  const res = await api.get(
                                    `/admin/campaigns/campaigns/${campaignDetail.campaign.id}/engagement/export`,
                                    { responseType: 'blob' }
                                  );
                                  const url = window.URL.createObjectURL(new Blob([res.data]));
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `campaign-${campaignDetail.campaign.id}-engagement.csv`;
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                } catch (err) {
                                  setError(err.response?.data?.error || t('adminCampaigns.loadError'));
                                } finally {
                                  setActionLoading(false);
                                }
                              }}
                            >
                              {t('adminCampaigns.exportEngagement')}
                            </Button>
                            <Button
                              size="small"
                              sx={adminSolidBtnSx}
                              disabled={actionLoading}
                              onClick={async () => {
                                try {
                                  setActionLoading(true);
                                  const res = await api.post(
                                    `/admin/campaigns/campaigns/${campaignDetail.campaign.id}/sync-engagement`,
                                    { limit: 1000 }
                                  );
                                  setSuccess(
                                    t('adminCampaigns.syncEngagementDone', {
                                      synced: res.data?.synced || 0,
                                      failed: res.data?.failed || 0,
                                    })
                                  );
                                  await loadCampaignDetail(campaignDetail.campaign.id);
                                } catch (err) {
                                  setError(err.response?.data?.error || t('adminCampaigns.loadError'));
                                } finally {
                                  setActionLoading(false);
                                }
                              }}
                            >
                              {t('adminCampaigns.syncToLeadCrm')}
                            </Button>
                          </Stack>
                        </Box>
                        <Typography variant="body2" sx={{ color: DOCUMENT.ink3, mb: 1.5 }}>
                          {t('adminCampaigns.engagementListHelp')}
                        </Typography>
                        {campaignDetail.recentEngagement?.length ? (
                          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, maxHeight: 360 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell>{t('adminCampaigns.email')}</TableCell>
                                  <TableCell>{t('adminCampaigns.company')}</TableCell>
                                  <TableCell>{t('adminCampaigns.event')}</TableCell>
                                  <TableCell>{t('adminCampaigns.when')}</TableCell>
                                  <TableCell>{t('adminCampaigns.leadCrmId')}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {campaignDetail.recentEngagement.map((row) => (
                                  <TableRow key={row.id}>
                                    <TableCell>{row.email}</TableCell>
                                    <TableCell>{row.company || '—'}</TableCell>
                                    <TableCell>
                                      {row.event_type === 'demo_click'
                                        ? t('adminCampaigns.eventDemoClick')
                                        : row.event_type === 'booked'
                                          ? t('adminCampaigns.eventBooked')
                                          : t('adminCampaigns.eventSiteVisit')}
                                    </TableCell>
                                    <TableCell>
                                      {row.created_at
                                        ? new Date(row.created_at).toLocaleString()
                                        : '—'}
                                    </TableCell>
                                    <TableCell>{row.leadcrm_lead_id || '—'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography variant="body2" sx={{ color: DOCUMENT.ink3 }}>
                            {t('adminCampaigns.noEngagementYet')}
                          </Typography>
                        )}
                      </Box>

                      <TextField
                        fullWidth
                        label={t('adminCampaigns.name')}
                        value={campaignDetail.campaign.name}
                        onChange={(e) =>
                          setCampaignDetail((prev) => ({
                            ...prev,
                            campaign: { ...prev.campaign, name: e.target.value },
                          }))
                        }
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label={t('adminCampaigns.subject')}
                        value={campaignDetail.campaign.subject}
                        onChange={(e) =>
                          setCampaignDetail((prev) => ({
                            ...prev,
                            campaign: { ...prev.campaign, subject: e.target.value },
                          }))
                        }
                        sx={{ mb: 2 }}
                        disabled={campaignDetail.campaign.status === 'running'}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label={t('adminCampaigns.emailsPerDay')}
                        value={campaignDetail.campaign.emails_per_day}
                        onChange={(e) =>
                          setCampaignDetail((prev) => ({
                            ...prev,
                            campaign: { ...prev.campaign, emails_per_day: Number(e.target.value) },
                          }))
                        }
                        sx={{ mb: 2 }}
                        inputProps={{ min: 1, max: 500 }}
                        helperText={t('adminCampaigns.emailsPerDayHelp')}
                      />

                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          border: `1px solid ${DOCUMENT.rule}`,
                          bgcolor: DOCUMENT.ivory,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: DOCUMENT.display,
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          {t('adminCampaigns.audienceSection')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: DOCUMENT.ink3, mb: 1.5 }}>
                          {Array.isArray(campaignDetail.campaign.audience_filter?.leadIds)
                            && campaignDetail.campaign.audience_filter.leadIds.length
                            ? t('adminCampaigns.audienceFromSelected', {
                                count: campaignDetail.campaign.audience_filter.leadIds.length,
                              })
                            : campaignDetail.campaign.audience_filter?.uploadId
                            ? t('adminCampaigns.audienceFromUpload', {
                                id: campaignDetail.campaign.audience_filter.uploadId,
                              })
                            : campaignDetail.campaign.audience_filter?.followUpCampaignId
                              ? t('adminCampaigns.audienceFollowUp', {
                                  id: campaignDetail.campaign.audience_filter.followUpCampaignId,
                                  segment:
                                    campaignDetail.campaign.audience_filter.followUpSegment ||
                                    'no_visit',
                                })
                              : t('adminCampaigns.audienceAllLeads')}
                          {campaignDetail.sends?.queued > 0
                            ? ` · ${t('adminCampaigns.queuedCount', {
                                count: campaignDetail.sends.queued,
                              })}`
                            : ''}
                        </Typography>
                        {['draft', 'paused'].includes(campaignDetail.campaign.status) ? (
                          <>
                            <Button
                              component="label"
                              startIcon={<UploadIcon />}
                              disabled={actionLoading}
                              sx={adminOutlineBtnSx}
                            >
                              {t('adminCampaigns.replaceAudienceFile')}
                              <input
                                key={audienceFileInputKey}
                                hidden
                                type="file"
                                accept=".xlsx,.xls,.csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                data-testid="campaign-replace-audience-file"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) replaceCampaignAudienceFile(file);
                                  e.target.value = '';
                                }}
                              />
                            </Button>
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ mt: 1, color: DOCUMENT.ink3 }}
                            >
                              {t('adminCampaigns.replaceAudienceHelp')}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="caption" sx={{ color: DOCUMENT.ink3 }}>
                            {t('adminCampaigns.replaceAudiencePausedOnly')}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                          {['draft', 'paused'].includes(campaignDetail.campaign.status) ? (
                            <Button
                              size="small"
                              onClick={rebuildCampaignQueueNow}
                              disabled={actionLoading}
                              sx={adminSolidBtnSx}
                            >
                              {t('adminCampaigns.rebuildQueue')}
                            </Button>
                          ) : null}
                          {campaignDetail.sends?.queued > 0 ? (
                            <Button
                              size="small"
                              onClick={openQueuedSendsDialog}
                              disabled={queuedSendsLoading}
                              sx={adminOutlineBtnSx}
                            >
                              {t('adminCampaigns.viewQueue')}
                            </Button>
                          ) : null}
                          {campaignDetail.sends?.failed > 0 ? (
                            <Button
                              size="small"
                              onClick={openFailedSendsDialog}
                              disabled={failedSendsLoading}
                              sx={{ ...adminOutlineBtnSx, color: '#c62828' }}
                            >
                              {t('adminCampaigns.viewFailures')}
                            </Button>
                          ) : null}
                        </Stack>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <EmailBodyTabs
                          tab={detailBodyTab}
                          onTabChange={(value) => {
                            setDetailBodyTab(value);
                            if (value === 'preview') loadDetailEmailPreview(detailPreviewSample);
                          }}
                          bodyHtml={campaignDetail.campaign.body_html}
                          onBodyHtmlChange={(value) =>
                            setCampaignDetail((prev) => ({
                              ...prev,
                              campaign: { ...prev.campaign, body_html: value },
                            }))
                          }
                          preview={detailPreview}
                          previewSample={detailPreviewSample}
                          previewLoading={detailPreviewLoading}
                          onLoadPreview={loadDetailEmailPreview}
                          disabled={campaignDetail.campaign.status === 'running'}
                          minRows={8}
                          helperText={t('adminCampaigns.mergeFieldsHelp', {
                            fields: (defaults?.mergeFields || []).map((f) => `{{${f}}}`).join(', '),
                          })}
                        />
                      </Box>
                      <Button onClick={saveCampaignEdits} disabled={actionLoading} sx={adminSolidBtnSx}>
                        {t('adminCampaigns.save')}
                      </Button>

                      {campaignDetail.recentFailures?.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            {t('adminCampaigns.recentFailures')}
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>{t('adminCampaigns.email')}</TableCell>
                                  <TableCell>{t('adminCampaigns.company')}</TableCell>
                                  <TableCell>{t('adminCampaigns.error')}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {campaignDetail.recentFailures.map((f) => (
                                  <TableRow key={f.id}>
                                    <TableCell>{f.email}</TableCell>
                                    <TableCell>{f.company}</TableCell>
                                    <TableCell>{f.error_message}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {tab === 1 && (
        <Card sx={adminSurfaceCardSx}>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
              {t('adminCampaigns.leadsBookHelp')}
              {' '}
              {t('adminCampaigns.leadsInactiveHelp')}
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                label={t('adminCampaigns.searchLeads')}
                value={leadQuery}
                onChange={(e) => setLeadQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchLeads(1)}
                sx={{ minWidth: 260 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t('adminCampaigns.leadStatusFilter')}</InputLabel>
                <Select
                  label={t('adminCampaigns.leadStatusFilter')}
                  value={leadsStatusFilter}
                  onChange={(e) => {
                    const next = e.target.value;
                    setLeadsStatusFilter(next);
                    searchLeads(1, { status: next });
                  }}
                >
                  <MenuItem value="active">{t('adminCampaigns.leadsActive')}</MenuItem>
                  <MenuItem value="inactive">{t('adminCampaigns.leadsInactive')}</MenuItem>
                  <MenuItem value="all">{t('adminCampaigns.leadsAll')}</MenuItem>
                </Select>
              </FormControl>
              <Button onClick={() => searchLeads(1)} disabled={actionLoading} sx={adminOutlineBtnSx}>
                {t('adminCampaigns.search')}
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                onClick={exportLeadsBook}
                disabled={actionLoading || leadsTotal === 0}
                sx={adminSolidBtnSx}
              >
                {t('adminCampaigns.exportLeads')}
              </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('adminCampaigns.leadsCount', { count: leadsTotal })}
                {leadsTotal > 0
                  ? ` · ${t('adminCampaigns.leadsPageRange', {
                      from: (leadsPage - 1) * leadsLimit + 1,
                      to: Math.min(leadsPage * leadsLimit, leadsTotal),
                      total: leadsTotal,
                    })}`
                  : ''}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  disabled={actionLoading || leadsPage <= 1}
                  onClick={() => searchLeads(leadsPage - 1)}
                  sx={adminOutlineBtnSx}
                >
                  {t('adminCampaigns.prevPage')}
                </Button>
                <Button
                  size="small"
                  disabled={actionLoading || leadsPage >= leadsTotalPages}
                  onClick={() => searchLeads(leadsPage + 1)}
                  sx={adminOutlineBtnSx}
                >
                  {t('adminCampaigns.nextPage')}
                </Button>
              </Stack>
            </Box>
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('adminCampaigns.company')}</TableCell>
                    <TableCell>{t('adminCampaigns.email')}</TableCell>
                    <TableCell>{t('adminCampaigns.contact')}</TableCell>
                    <TableCell>{t('adminCampaigns.phone')}</TableCell>
                    <TableCell>{t('adminCampaigns.priority')}</TableCell>
                    <TableCell>{t('adminCampaigns.country')}</TableCell>
                    <TableCell>{t('adminCampaigns.type')}</TableCell>
                    <TableCell>{t('adminCampaigns.status')}</TableCell>
                    <TableCell align="right">{t('adminCampaigns.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow
                      key={lead.id}
                      sx={lead.inactive_at ? { opacity: 0.65 } : undefined}
                    >
                      <TableCell>{lead.company}</TableCell>
                      <TableCell>{lead.email}</TableCell>
                      <TableCell>{lead.contact_name || t('adminCampaigns.genericContact')}</TableCell>
                      <TableCell>{lead.phone || '—'}</TableCell>
                      <TableCell>{lead.priority || '—'}</TableCell>
                      <TableCell>{lead.country || '—'}</TableCell>
                      <TableCell>{lead.type || '—'}</TableCell>
                      <TableCell>
                        {lead.inactive_at
                          ? t('adminCampaigns.inactive')
                          : lead.unsubscribed_at
                            ? t('adminCampaigns.unsubscribed')
                            : lead.bounced_at
                              ? t('adminCampaigns.bounced')
                              : t('adminCampaigns.active')}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {lead.inactive_at ? (
                            <Button
                              size="small"
                              startIcon={<ReactivateIcon />}
                              disabled={actionLoading}
                              onClick={() => setLeadInactiveState(lead, false)}
                              sx={{ ...adminOutlineBtnSx, py: 0.5, px: 1.5 }}
                            >
                              {t('adminCampaigns.reactivateLead')}
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              startIcon={<InactiveIcon />}
                              disabled={actionLoading}
                              onClick={() => setLeadInactiveState(lead, true)}
                              sx={{ ...adminOutlineBtnSx, py: 0.5, px: 1.5 }}
                            >
                              {t('adminCampaigns.deactivateLead')}
                            </Button>
                          )}
                          <Button
                            size="small"
                            startIcon={<DeleteIcon />}
                            disabled={actionLoading}
                            onClick={() => deleteLeadRow(lead)}
                            sx={{ ...adminOutlineBtnSx, py: 0.5, px: 1.5 }}
                          >
                            {t('adminCampaigns.deleteLead')}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {leads.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography color="text.secondary">{t('adminCampaigns.noLeads')}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {tab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={adminSurfaceCardSx}>
              <CardContent>
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.25rem', color: DOCUMENT.ink }}>
                    {t('adminCampaigns.templates')}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={startBlankTemplateDraft}
                    sx={adminSolidBtnSx}
                  >
                    {t('adminCampaigns.newEmailTemplate')}
                  </Button>
                </Box>
                <Stack spacing={1}>
                  {!templateDraft?.id && templateDraft ? (
                    <Paper
                      sx={{
                        p: 1.5,
                        borderRadius: 0,
                        border: `1px solid ${DOCUMENT.oxblood}`,
                        boxShadow: 'none',
                        bgcolor: `color-mix(in srgb, ${DOCUMENT.oxblood} 8%, ${DOCUMENT.surface})`,
                      }}
                    >
                      <Typography fontWeight={600}>
                        {templateDraft.name?.trim() || t('adminCampaigns.newEmailTemplate')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('adminCampaigns.unsavedTemplate')}
                      </Typography>
                    </Paper>
                  ) : null}
                  {templates.map((tpl) => (
                    <Paper
                      key={tpl.id}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        borderRadius: 0,
                        border: `1px solid ${DOCUMENT.ruleHard}`,
                        boxShadow: 'none',
                        bgcolor: selectedTemplateId === tpl.id
                          ? `color-mix(in srgb, ${DOCUMENT.oxblood} 8%, ${DOCUMENT.surface})`
                          : DOCUMENT.surface,
                      }}
                      onClick={() => {
                        setSelectedTemplateId(tpl.id);
                        setTemplateDraft(tpl);
                      }}
                    >
                      <Typography fontWeight={600}>{tpl.name}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {tpl.description || tpl.slug}
                      </Typography>
                      <Button
                        size="small"
                        sx={{ ...adminOutlineBtnSx, mt: 1, py: 0.75 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openCreateWithTemplate(tpl);
                        }}
                      >
                        {t('adminCampaigns.useTemplate')}
                      </Button>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Card sx={adminSurfaceCardSx}>
              <CardContent>
                {!templateDraft ? (
                  <Typography color="text.secondary">{t('adminCampaigns.selectTemplate')}</Typography>
                ) : (
                  <Stack spacing={2}>
                    <TextField
                      label={t('adminCampaigns.name')}
                      value={templateDraft.name || ''}
                      onChange={(e) => setTemplateDraft((p) => ({ ...p, name: e.target.value }))}
                    />
                    <TextField
                      label={t('adminCampaigns.templateDescription')}
                      value={templateDraft.description || ''}
                      onChange={(e) =>
                        setTemplateDraft((p) => ({ ...p, description: e.target.value }))
                      }
                    />
                    <TextField
                      label={t('adminCampaigns.subject')}
                      value={templateDraft.subject || ''}
                      onChange={(e) => setTemplateDraft((p) => ({ ...p, subject: e.target.value }))}
                      helperText={t('adminCampaigns.mergeFieldsHelp', {
                        fields: (defaults?.mergeFields || [])
                          .slice(0, 6)
                          .map((f) => `{{${f}}}`)
                          .join(', '),
                      })}
                    />
                    <TextField
                      multiline
                      minRows={14}
                      label={t('adminCampaigns.bodyHtml')}
                      value={templateDraft.body_html || ''}
                      onChange={(e) =>
                        setTemplateDraft((p) => ({ ...p, body_html: e.target.value }))
                      }
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        startIcon={<PreviewIcon />}
                        onClick={() => previewTemplateDraft('named')}
                        disabled={actionLoading}
                        sx={adminOutlineBtnSx}
                      >
                        {t('adminCampaigns.previewEmail')}
                      </Button>
                      <Button onClick={saveTemplateDraft} disabled={actionLoading} sx={adminSolidBtnSx}>
                        {templateDraft.id
                          ? t('adminCampaigns.saveTemplate')
                          : t('adminCampaigns.createTemplate')}
                      </Button>
                      {templateDraft.id && templateDraft.is_builtin ? (
                        <Button onClick={resetTemplateDraft} disabled={actionLoading} sx={adminOutlineBtnSx}>
                          {t('adminCampaigns.resetTemplate')}
                        </Button>
                      ) : null}
                      {templateDraft.id ? (
                        <Button onClick={() => openCreateWithTemplate(templateDraft)} sx={adminOutlineBtnSx}>
                          {t('adminCampaigns.useTemplate')}
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Dialog
        open={emailPreviewOpen}
        onClose={() => setEmailPreviewOpen(false)}
        fullWidth
        maxWidth="md"
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: undefined },
            maxHeight: { xs: 'calc(100% - 16px)', sm: undefined },
          },
        }}
      >
        <DialogTitle>{t('adminCampaigns.emailPreviewTitle')}</DialogTitle>
        <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}>
          {emailPreview ? (
            <Box sx={{ mt: 1, width: '100%', minWidth: 0 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  onClick={() => previewTemplateDraft('named')}
                  disabled={actionLoading}
                  sx={emailPreviewSample === 'named' ? adminSolidBtnSx : adminOutlineBtnSx}
                >
                  {t('adminCampaigns.previewSampleNamed')}
                </Button>
                <Button
                  size="small"
                  onClick={() => previewTemplateDraft('generic')}
                  disabled={actionLoading}
                  sx={emailPreviewSample === 'generic' ? adminSolidBtnSx : adminOutlineBtnSx}
                >
                  {t('adminCampaigns.previewSampleGeneric')}
                </Button>
              </Stack>
              <Typography
                variant="body2"
                sx={{ mb: 1, color: DOCUMENT.ink3, wordBreak: 'break-word' }}
              >
                {t('adminCampaigns.emailPreviewMeta', {
                  company: emailPreview.sampleLead?.company || '—',
                  contact: emailPreview.sampleLead?.contact_name || t('adminCampaigns.genericContact'),
                  greeting: emailPreview.greeting || '—',
                })}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: DOCUMENT.ink,
                  wordBreak: 'break-word',
                }}
              >
                {emailPreview.subject}
              </Typography>
              <Box
                sx={{
                  border: `1px solid ${DOCUMENT.ruleHard}`,
                  bgcolor: DOCUMENT.ivory,
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                <EmailPreviewFrame
                  html={emailPreview.html}
                  title={t('adminCampaigns.emailPreviewTitle')}
                  height={520}
                />
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailPreviewOpen(false)} sx={adminOutlineBtnSx}>
            {t('adminCampaigns.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={uploadPreviewOpen}
        onClose={() => !actionLoading && closeUploadPreview()}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{t('adminCampaigns.uploadPreviewTitle')}</DialogTitle>
        <DialogContent>
          {uploadPreview ? (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ mb: 2, color: DOCUMENT.ink3 }}>
                {t('adminCampaigns.uploadPreviewSubtitle', { filename: uploadPreview.filename })}
              </Typography>

              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {[
                  { label: t('adminCampaigns.previewRows'), value: uploadPreview.rowCount },
                  { label: t('adminCampaigns.previewValid'), value: uploadPreview.validCount },
                  { label: t('adminCampaigns.previewNew'), value: uploadPreview.newEstimate },
                  { label: t('adminCampaigns.previewExisting'), value: uploadPreview.alreadyInBookCount },
                  { label: t('adminCampaigns.previewFileDupes'), value: uploadPreview.fileDuplicateCount },
                  { label: t('adminCampaigns.previewInvalid'), value: uploadPreview.invalidCount },
                ].map((stat) => (
                  <Grid item xs={6} sm={4} md={2} key={stat.label}>
                    <Paper sx={adminStatPaperSx}>
                      <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.35rem', color: DOCUMENT.ink, textAlign: 'center' }}>
                        {stat.value}
                      </Typography>
                      <Typography sx={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3, textAlign: 'center', mt: 0.5 }}>
                        {stat.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {uploadPreview.columnMap && (
                <Typography variant="body2" sx={{ mb: 2, color: DOCUMENT.ink3 }}>
                  {t('adminCampaigns.previewMappedColumns', {
                    company: uploadPreview.columnMap.company || '—',
                    email: uploadPreview.columnMap.email || '—',
                    name: uploadPreview.columnMap.contact_name || '—',
                  })}
                </Typography>
              )}

              <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
                {t('adminCampaigns.uploadPreviewHelp')}
              </Alert>

              <TableContainer component={Paper} sx={{ ...adminSurfaceCardSx, maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('adminCampaigns.company')}</TableCell>
                      <TableCell>{t('adminCampaigns.contact')}</TableCell>
                      <TableCell>{t('adminCampaigns.email')}</TableCell>
                      <TableCell>{t('adminCampaigns.priority')}</TableCell>
                      <TableCell>{t('adminCampaigns.country')}</TableCell>
                      <TableCell>{t('adminCampaigns.previewStatus')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(uploadPreview.leads || []).map((lead) => (
                      <TableRow key={`${lead.email}-${lead.company}`}>
                        <TableCell>{lead.company}</TableCell>
                        <TableCell>
                        {lead.is_generic_contact
                          ? t('adminCampaigns.genericContact')
                          : (lead.contact_name || '—')}
                        {lead.original_contact_name ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {lead.original_contact_name}
                          </Typography>
                        ) : null}
                        {lead.greeting ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {t('adminCampaigns.previewGreeting', { greeting: lead.greeting })}
                          </Typography>
                        ) : null}
                      </TableCell>
                        <TableCell>{lead.email}</TableCell>
                        <TableCell>{lead.priority || '—'}</TableCell>
                        <TableCell>{lead.country || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              lead.alreadyInBook
                                ? t('adminCampaigns.previewExistingLead')
                                : t('adminCampaigns.previewNewLead')
                            }
                            color={lead.alreadyInBook ? 'default' : 'success'}
                            sx={{ borderRadius: 0 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadPreview} disabled={actionLoading} sx={adminOutlineBtnSx}>
            {t('adminCampaigns.cancel')}
          </Button>
          <Button
            onClick={confirmUpload}
            disabled={actionLoading || !uploadPreview?.validCount}
            sx={adminSolidBtnSx}
          >
            {actionLoading ? t('adminCampaigns.importing') : t('adminCampaigns.confirmImport', { count: uploadPreview?.validCount || 0 })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createOpen}
        onClose={closeCreateDialog}
        fullWidth
        maxWidth="md"
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: undefined },
            maxHeight: { xs: 'calc(100% - 16px)', sm: undefined },
          },
        }}
      >
        <DialogTitle>{t('adminCampaigns.newCampaign')}</DialogTitle>
        <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              required
              label={t('adminCampaigns.name')}
              value={createForm.name}
              onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>{t('adminCampaigns.emailTemplate')}</InputLabel>
              <Select
                label={t('adminCampaigns.emailTemplate')}
                value={createForm.templateId}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === NEW_TEMPLATE_VALUE) {
                    startNewTemplateInCreateForm();
                    return;
                  }
                  const tpl = templates.find((item) => String(item.id) === String(value));
                  if (tpl) applyTemplateToCreateForm(tpl);
                  else setCreateForm((p) => ({ ...p, templateId: value, templateName: '' }));
                }}
                renderValue={(value) => {
                  if (value === NEW_TEMPLATE_VALUE) {
                    return t('adminCampaigns.newEmailTemplate');
                  }
                  const tpl = templates.find((item) => String(item.id) === String(value));
                  return tpl?.name || t('adminCampaigns.emailTemplate');
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 0,
                      border: `1px solid ${DOCUMENT.rule}`,
                      bgcolor: DOCUMENT.canvas,
                      maxHeight: 240,
                    },
                  },
                }}
              >
                <MenuItem
                  value={NEW_TEMPLATE_VALUE}
                  sx={{
                    fontWeight: 700,
                    color: DOCUMENT.oxblood,
                    borderBottom: `1px solid ${DOCUMENT.rule}`,
                  }}
                >
                  {t('adminCampaigns.newEmailTemplate')}
                </MenuItem>
                {templates.map((tpl) => (
                  <MenuItem key={tpl.id} value={String(tpl.id)}>
                    {tpl.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {createForm.templateId === NEW_TEMPLATE_VALUE ? (
              <TextField
                required
                label={t('adminCampaigns.templateName')}
                value={createForm.templateName}
                onChange={(e) => setCreateForm((p) => ({ ...p, templateName: e.target.value }))}
                helperText={t('adminCampaigns.newTemplateNameHelp')}
              />
            ) : null}
            <TextField
              label={t('adminCampaigns.subject')}
              value={createForm.subject}
              onChange={(e) => setCreateForm((p) => ({ ...p, subject: e.target.value }))}
              helperText={t('adminCampaigns.mergeFieldsHelp', {
                fields: '{{first_name}}, {{company_name}}, {{personalization}}, {{demoCtaUrl}}, {{partnerCtaUrl}}',
              })}
            />
            <TextField
              type="number"
              label={t('adminCampaigns.emailsPerDay')}
              value={createForm.emailsPerDay}
              onChange={(e) => setCreateForm((p) => ({ ...p, emailsPerDay: e.target.value }))}
              inputProps={{ min: 1, max: 500 }}
              helperText={t('adminCampaigns.emailsPerDayHelp')}
            />
            <FormControl component="fieldset">
              <FormLabel component="legend">{t('adminCampaigns.audienceSource')}</FormLabel>
              <RadioGroup
                value={createForm.audienceMode}
                onChange={(e) => {
                  const nextMode = e.target.value;
                  setCreateForm((p) => ({
                    ...p,
                    audienceMode: nextMode,
                    audienceFile: nextMode === 'file' ? p.audienceFile : null,
                    selectedLeads: nextMode === 'select' ? p.selectedLeads : [],
                  }));
                  if (nextMode === 'select') {
                    resetAudienceLeadFilters();
                    loadAudienceLeadOptions('');
                  } else {
                    resetAudienceLeadFilters();
                  }
                }}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio inputProps={{ 'data-testid': 'campaign-audience-mode-all' }} />}
                  label={t('adminCampaigns.allLeads')}
                />
                <FormControlLabel
                  value="select"
                  control={<Radio inputProps={{ 'data-testid': 'campaign-audience-mode-select' }} />}
                  label={t('adminCampaigns.audienceFromExisting')}
                />
                <FormControlLabel
                  value="file"
                  control={<Radio inputProps={{ 'data-testid': 'campaign-audience-mode-file' }} />}
                  label={t('adminCampaigns.audienceFromFile')}
                />
              </RadioGroup>
            </FormControl>
            {createForm.audienceMode === 'select' ? (
              <Box data-testid="campaign-audience-lead-picker">
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems="flex-start"
                  sx={{ mb: 0.5 }}
                >
                  <FormControl
                    size="small"
                    sx={{ ...audienceFilterSelectSx, flex: '1 1 110px', minWidth: 110, maxWidth: 160 }}
                    data-testid="campaign-audience-type-filter"
                  >
                    <InputLabel shrink>{t('adminCampaigns.typeFilter')}</InputLabel>
                    <Select
                      notched
                      label={t('adminCampaigns.typeFilter')}
                      value={audienceLeadTypeFilter}
                      displayEmpty
                      onChange={(e) => changeAudienceLeadTypeFilter(e.target.value)}
                      MenuProps={documentSelectMenuProps}
                    >
                      <MenuItem value="">{t('adminCampaigns.anyType')}</MenuItem>
                      {audienceLeadTypes.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl
                    size="small"
                    sx={{ ...audienceFilterSelectSx, flex: '1 1 100px', minWidth: 100, maxWidth: 140 }}
                    data-testid="campaign-audience-priority-filter"
                  >
                    <InputLabel shrink>{t('adminCampaigns.priorityFilterShort')}</InputLabel>
                    <Select
                      notched
                      label={t('adminCampaigns.priorityFilterShort')}
                      value={audienceLeadPriorityFilter}
                      displayEmpty
                      onChange={(e) => changeAudienceLeadPriorityFilter(e.target.value)}
                      MenuProps={documentSelectMenuProps}
                    >
                      <MenuItem value="">{t('adminCampaigns.anyPriority')}</MenuItem>
                      {(audienceLeadPriorities.length
                        ? audienceLeadPriorities
                        : ['A+', 'A', 'B']
                      ).map((priority) => (
                        <MenuItem key={priority} value={priority}>{priority}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl
                    size="small"
                    sx={{ ...audienceFilterSelectSx, flex: '1 1 120px', minWidth: 120, maxWidth: 180 }}
                    data-testid="campaign-audience-country-filter"
                  >
                    <InputLabel shrink>{t('adminCampaigns.countryFilter')}</InputLabel>
                    <Select
                      notched
                      label={t('adminCampaigns.countryFilter')}
                      value={audienceLeadCountryFilter}
                      displayEmpty
                      onChange={(e) => changeAudienceLeadCountryFilter(e.target.value)}
                      MenuProps={documentSelectMenuProps}
                    >
                      <MenuItem value="">{t('adminCampaigns.anyCountry')}</MenuItem>
                      {audienceLeadCountries.map((country) => (
                        <MenuItem key={country} value={country}>{country}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label={t('adminCampaigns.searchExistingLeads')}
                    placeholder={t('adminCampaigns.searchExistingLeadsHint')}
                    value={audienceLeadSearchInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAudienceLeadSearchInput(value);
                      scheduleAudienceLeadSearch(value);
                    }}
                    inputProps={{ 'data-testid': 'campaign-audience-lead-search' }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      ...audienceFilterSelectSx,
                      flex: '2 1 180px',
                      minWidth: 160,
                      '& .MuiInputBase-root': { minHeight: 36 },
                    }}
                  />
                </Stack>
                <Typography variant="caption" display="block" sx={{ mb: 1.5, color: DOCUMENT.ink3 }}>
                  {t('adminCampaigns.audienceFromExistingHelp', {
                    count: (createForm.selectedLeads || []).length,
                    visible: audienceVisibleSelectedCount,
                    matched: audienceMatchTotal,
                  })}
                </Typography>
                <Box sx={audienceLeadListPanelSx} data-testid="campaign-audience-lead-list">
                  <Box
                    data-testid="campaign-audience-select-all-option"
                    onClick={() => {
                      if (!audienceLeadsLoading && !audienceSelectAllLoading && audienceMatchTotal > 0) {
                        toggleAudienceSelectAll();
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      borderBottom: `1px solid ${DOCUMENT.rule}`,
                      cursor: audienceMatchTotal === 0 ? 'default' : 'pointer',
                      bgcolor: DOCUMENT.canvas,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <Checkbox
                      size="small"
                      icon={audienceLeadCheckboxIcon}
                      checkedIcon={audienceLeadCheckboxCheckedIcon}
                      checked={audienceSelectAllState.all && audienceMatchTotal > 0}
                      indeterminate={
                        audienceSelectAllState.some
                        || ((createForm.selectedLeads || []).length > 0 && !audienceSelectAllState.all)
                      }
                      disabled={audienceLeadsLoading || audienceSelectAllLoading || audienceMatchTotal === 0}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'data-testid': 'campaign-audience-select-all' }}
                    />
                    <Typography sx={{ fontSize: 13, color: DOCUMENT.ink, fontFamily: DOCUMENT.sans }}>
                      {audienceSelectAllLoading
                        ? t('adminCampaigns.selectingLeads')
                        : t('adminCampaigns.selectAllMatching', { count: audienceMatchTotal })}
                    </Typography>
                  </Box>
                  <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
                    {audienceLeadsLoading && !audienceLeadOptions.length ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={22} sx={{ color: DOCUMENT.oxblood }} />
                      </Box>
                    ) : null}
                    {!audienceLeadsLoading && !audienceLeadOptions.length ? (
                      <Typography sx={{ px: 2, py: 1.5, fontSize: 13, color: DOCUMENT.ink3 }}>
                        {t('adminCampaigns.noMatchingLeads')}
                      </Typography>
                    ) : null}
                    {audienceLeadOptions.map((option) => {
                      const selected = audienceSelectedIdSet.has(Number(option.id));
                      return (
                        <Box
                          key={option.id}
                          onClick={() => toggleAudienceLead(option)}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            px: 2,
                            py: 1,
                            cursor: 'pointer',
                            bgcolor: selected ? DOCUMENT.ivory : 'transparent',
                            '&:hover': { bgcolor: DOCUMENT.ivory },
                          }}
                        >
                          <Checkbox
                            size="small"
                            icon={audienceLeadCheckboxIcon}
                            checkedIcon={audienceLeadCheckboxCheckedIcon}
                            checked={selected}
                            tabIndex={-1}
                            disableRipple
                            sx={{ pt: 0.25 }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, color: DOCUMENT.ink, lineHeight: 1.3 }}>
                              {option.company || option.email}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, lineHeight: 1.3 }}>
                              {[option.type, option.contact_name, option.email, option.priority]
                                .filter(Boolean)
                                .join(' · ')}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            ) : null}
            {createForm.audienceMode === 'file' ? (
              <Box>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={adminOutlineBtnSx}
                  disabled={actionLoading}
                >
                  {createForm.audienceFile?.name || t('adminCampaigns.chooseAudienceFile')}
                  <input
                    hidden
                    type="file"
                    accept=".xlsx,.xls,.csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    data-testid="campaign-audience-file"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setCreateForm((p) => ({ ...p, audienceFile: file }));
                      e.target.value = '';
                    }}
                  />
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 1, color: DOCUMENT.ink3 }}>
                  {t('adminCampaigns.audienceFileHelp')}
                </Typography>
              </Box>
            ) : null}
            <FormControl fullWidth>
              <InputLabel>{t('adminCampaigns.priorityFilter')}</InputLabel>
              <Select
                label={t('adminCampaigns.priorityFilter')}
                value={createForm.priority}
                onChange={(e) => setCreateForm((p) => ({ ...p, priority: e.target.value }))}
              >
                <MenuItem value="">{t('adminCampaigns.anyPriority')}</MenuItem>
                <MenuItem value="A+">A+</MenuItem>
                <MenuItem value="A">A</MenuItem>
                <MenuItem value="B">B</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('adminCampaigns.followUpFrom')}</InputLabel>
              <Select
                label={t('adminCampaigns.followUpFrom')}
                value={createForm.followUpCampaignId}
                onChange={(e) =>
                  setCreateForm((p) => ({
                    ...p,
                    followUpCampaignId: e.target.value,
                    excludeEmailed: e.target.value ? false : p.excludeEmailed,
                    followUpSegment: e.target.value ? p.followUpSegment || 'no_visit' : '',
                  }))
                }
              >
                <MenuItem value="">{t('adminCampaigns.notFollowUp')}</MenuItem>
                {campaigns.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name} (#{c.id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {createForm.followUpCampaignId ? (
              <FormControl fullWidth>
                <InputLabel>{t('adminCampaigns.followUpSegment')}</InputLabel>
                <Select
                  label={t('adminCampaigns.followUpSegment')}
                  value={createForm.followUpSegment || 'no_visit'}
                  onChange={(e) => setCreateForm((p) => ({ ...p, followUpSegment: e.target.value }))}
                >
                  <MenuItem value="no_visit">{t('adminCampaigns.segmentNoVisit')}</MenuItem>
                  <MenuItem value="visited_no_booking">{t('adminCampaigns.segmentVisitedNoBooking')}</MenuItem>
                  <MenuItem value="site_visits">{t('adminCampaigns.segmentSiteVisits')}</MenuItem>
                  <MenuItem value="demo_clicks">{t('adminCampaigns.segmentDemoClicks')}</MenuItem>
                  <MenuItem value="bookings">{t('adminCampaigns.segmentBookings')}</MenuItem>
                </Select>
              </FormControl>
            ) : null}
            <FormControlLabel
              control={
                <Switch
                  checked={createForm.excludeEmailed}
                  onChange={(e) => setCreateForm((p) => ({ ...p, excludeEmailed: e.target.checked }))}
                  disabled={Boolean(createForm.followUpCampaignId)}
                />
              }
              label={t('adminCampaigns.excludeEmailed')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createForm.excludePartnerAccounts}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, excludePartnerAccounts: e.target.checked }))
                  }
                />
              }
              label={t('adminCampaigns.excludePartners')}
            />
            <EmailBodyTabs
              tab={createBodyTab}
              onTabChange={(value) => {
                setCreateBodyTab(value);
                if (value === 'preview') loadCreateEmailPreview(createPreviewSample);
              }}
              bodyHtml={createForm.bodyHtml}
              onBodyHtmlChange={(value) => setCreateForm((p) => ({ ...p, bodyHtml: value }))}
              preview={createPreview}
              previewSample={createPreviewSample}
              previewLoading={createPreviewLoading}
              onLoadPreview={loadCreateEmailPreview}
              minRows={10}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} sx={adminOutlineBtnSx}>{t('adminCampaigns.cancel')}</Button>
          <Button
            onClick={createCampaign}
            disabled={
              actionLoading ||
              !createForm.name.trim() ||
              (createForm.audienceMode === 'file' && !createForm.audienceFile) ||
              (createForm.audienceMode === 'select' && !(createForm.selectedLeads || []).length)
            }
            sx={adminSolidBtnSx}
          >
            {t('adminCampaigns.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(engagementFollowUp) || engagementFollowUpLoading}
        onClose={() => !actionLoading && setEngagementFollowUp(null)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: undefined },
            maxHeight: { xs: 'calc(100% - 16px)', sm: undefined },
          },
        }}
      >
        <DialogTitle>
          {engagementFollowUp?.templateName
            || t('adminCampaigns.engagementFollowUpTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}>
          {engagementFollowUpLoading || !engagementFollowUp ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: DOCUMENT.ink3 }}>
                {t('adminCampaigns.engagementFollowUpHelp', {
                  campaign: engagementFollowUp.sourceCampaignName,
                  count: engagementFollowUp.contacts.length,
                })}
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, maxHeight: 240 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={
                            engagementFollowUp.contacts.length > 0
                            && engagementFollowUp.selectedLeadIds.length === engagementFollowUp.contacts.length
                          }
                          indeterminate={
                            engagementFollowUp.selectedLeadIds.length > 0
                            && engagementFollowUp.selectedLeadIds.length < engagementFollowUp.contacts.length
                          }
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setEngagementFollowUp((prev) => ({
                              ...prev,
                              selectedLeadIds: checked
                                ? prev.contacts.map((c) => c.lead_id)
                                : [],
                            }));
                          }}
                        />
                      </TableCell>
                      <TableCell>{t('adminCampaigns.email')}</TableCell>
                      <TableCell>{t('adminCampaigns.company')}</TableCell>
                      <TableCell>{t('adminCampaigns.event')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {engagementFollowUp.contacts.map((row) => {
                      const checked = engagementFollowUp.selectedLeadIds.includes(row.lead_id);
                      return (
                        <TableRow key={`${row.lead_id}-${row.event_type}`} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={checked}
                              onChange={() => {
                                setEngagementFollowUp((prev) => {
                                  const set = new Set(prev.selectedLeadIds);
                                  if (set.has(row.lead_id)) set.delete(row.lead_id);
                                  else set.add(row.lead_id);
                                  return { ...prev, selectedLeadIds: [...set] };
                                });
                              }}
                            />
                          </TableCell>
                          <TableCell>{row.email}</TableCell>
                          <TableCell>{row.company || '—'}</TableCell>
                          <TableCell>
                            {row.event_type === 'demo_click'
                              ? t('adminCampaigns.eventDemoClick')
                              : row.event_type === 'booked'
                                ? t('adminCampaigns.eventBooked')
                                : t('adminCampaigns.eventSiteVisit')}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TextField
                fullWidth
                label={t('adminCampaigns.subject')}
                value={engagementFollowUp.subject}
                onChange={(e) =>
                  setEngagementFollowUp((prev) => ({ ...prev, subject: e.target.value }))
                }
              />
              <EmailBodyTabs
                tab={engagementBodyTab}
                onTabChange={(value) => {
                  setEngagementBodyTab(value);
                  if (value === 'preview') loadEngagementPreview(engagementPreviewSample);
                }}
                bodyHtml={engagementFollowUp.bodyHtml}
                onBodyHtmlChange={(value) =>
                  setEngagementFollowUp((prev) => ({ ...prev, bodyHtml: value }))
                }
                preview={engagementPreview}
                previewSample={engagementPreviewSample}
                previewLoading={engagementPreviewLoading}
                onLoadPreview={loadEngagementPreview}
                minRows={8}
                iframeHeight={320}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEngagementFollowUp(null)}
            disabled={actionLoading}
            sx={adminOutlineBtnSx}
          >
            {t('adminCampaigns.cancel')}
          </Button>
          <Button
            onClick={sendEngagementFollowUp}
            disabled={
              actionLoading
              || engagementFollowUpLoading
              || !engagementFollowUp?.selectedLeadIds?.length
            }
            startIcon={<SendIcon />}
            sx={adminSolidBtnSx}
          >
            {t('adminCampaigns.sendFollowUp', {
              count: engagementFollowUp?.selectedLeadIds?.length || 0,
            })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(queuedSendsDialog) || queuedSendsLoading}
        onClose={() => setQueuedSendsDialog(null)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: undefined },
            maxHeight: { xs: 'calc(100% - 16px)', sm: undefined },
          },
        }}
      >
        <DialogTitle>{t('adminCampaigns.queuedSendsTitle')}</DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}>
          {queuedSendsLoading || !queuedSendsDialog ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ color: DOCUMENT.ink3, mb: 2 }}>
                {t('adminCampaigns.queuedSendsHelp', {
                  campaign: queuedSendsDialog.campaignName,
                  count: queuedSendsDialog.total,
                  shown: queuedSendsDialog.queue.length,
                })}
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('adminCampaigns.email')}</TableCell>
                      <TableCell>{t('adminCampaigns.company')}</TableCell>
                      <TableCell>{t('adminCampaigns.contact')}</TableCell>
                      <TableCell>{t('adminCampaigns.priority')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {queuedSendsDialog.queue.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.company || '—'}</TableCell>
                        <TableCell>{row.contact_name || '—'}</TableCell>
                        <TableCell>{row.priority || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQueuedSendsDialog(null)} sx={adminOutlineBtnSx}>
            {t('adminCampaigns.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(failedSendsDialog) || failedSendsLoading}
        onClose={() => !actionLoading && setFailedSendsDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('adminCampaigns.failedSendsTitle')}</DialogTitle>
        <DialogContent dividers>
          {failedSendsLoading || !failedSendsDialog ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: DOCUMENT.ink3 }}>
                {t('adminCampaigns.failedSendsHelp', {
                  campaign: failedSendsDialog.campaignName,
                  count: failedSendsDialog.failures.length,
                  requeueable: failedSendsDialog.requeueableCount,
                })}
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={
                            failedSendsDialog.requeueableCount > 0
                            && failedSendsDialog.selectedSendIds.length === failedSendsDialog.requeueableCount
                          }
                          indeterminate={
                            failedSendsDialog.selectedSendIds.length > 0
                            && failedSendsDialog.selectedSendIds.length < failedSendsDialog.requeueableCount
                          }
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFailedSendsDialog((prev) => ({
                              ...prev,
                              selectedSendIds: checked
                                ? prev.failures.filter((f) => f.requeueable).map((f) => f.id)
                                : [],
                            }));
                          }}
                        />
                      </TableCell>
                      <TableCell>{t('adminCampaigns.email')}</TableCell>
                      <TableCell>{t('adminCampaigns.company')}</TableCell>
                      <TableCell>{t('adminCampaigns.error')}</TableCell>
                      <TableCell>{t('adminCampaigns.when')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {failedSendsDialog.failures.map((row) => {
                      const checked = failedSendsDialog.selectedSendIds.includes(row.id);
                      const blocked = (row.blockedReasons || []).join(', ');
                      return (
                        <TableRow key={row.id} hover selected={checked}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={checked}
                              disabled={!row.requeueable}
                              onChange={() => {
                                if (!row.requeueable) return;
                                setFailedSendsDialog((prev) => {
                                  const set = new Set(prev.selectedSendIds);
                                  if (set.has(row.id)) set.delete(row.id);
                                  else set.add(row.id);
                                  return { ...prev, selectedSendIds: [...set] };
                                });
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.email}</Typography>
                            {!row.requeueable && (
                              <Typography variant="caption" color="error">
                                {t('adminCampaigns.failedNotRequeueable', { reason: blocked || 'blocked' })}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{row.company || '—'}</TableCell>
                          <TableCell sx={{ maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {row.error_message || '—'}
                          </TableCell>
                          <TableCell>
                            {row.updated_at
                              ? new Date(row.updated_at).toLocaleString()
                              : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailedSendsDialog(null)} disabled={actionLoading}>
            {t('adminCampaigns.cancel')}
          </Button>
          <Button
            onClick={requeueSelectedFailures}
            disabled={
              actionLoading
              || failedSendsLoading
              || !failedSendsDialog?.selectedSendIds?.length
            }
            sx={adminSolidBtnSx}
          >
            {t('adminCampaigns.failedRequeueSelected', {
              count: failedSendsDialog?.selectedSendIds?.length || 0,
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminChrome>
  );
};

export default AdminCampaigns;
